import React, { lazy, Suspense, useRef } from 'react';
import {
  Empty,
  Grid,
  Heading,
  Icon,
  LinkAsButton,
  scrollIntoView,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { Experiment, FeatureToggle } from '@printdeal/feature-management/react';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { SelectorContent } from '@printdeal/product-selector';
import { Markdown } from '@printdeal/markdown/react';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { ProductTabs } from './ProductTabs';
import { ReviewStars } from '../../../review-stars/ReviewStars';
import ClientOnly from '../../../ClientOnly';
import type { IProductPage, IPageData } from '../../../../types/product/productDetailPage';
import { REVIEWS_IO_THRESHOLD } from '../../../../helpers/reviewHelper';
import LinkedFaqs from '../LinkedFaqs';
import { ProductGalleryBuilder } from '../ProductGalleryBuilder';
import { getConfiguration } from '../../../../helpers/Configuration';
import { OrderProgress } from '../OrderProgress';
import PersonalAssistance from '../PersonalAssistance';
import { ExploreDesignTemplateButton } from '../../../design-template/exploreDesignTemplateButton';
import { FallbackSpinnerComponent } from '../../../FallbackSpinnerComponent';
import { useSaveUserSelection } from '../useSelectionOnWindow';
import { usePagesExperiment } from '../../../../hooks/usePagesExperiment';
import { DiscountLabels } from '../DiscountLabels';
import { StepByStepSelectorSection } from '../../selector/components/StepByStep/StepByStepSelectorSection';

const QuoteRequestSection = lazy(() => import(/* webpackChunkName: "quote-request-section" */ '../../../widgets/QuoteRequestSection'));
const ReviewsIO = lazy(() => import(/* webpackChunkName: "reviews-io" */ '../reviews/ReviewsIO'));
const StaticReviews = lazy(() => import(/* webpackChunkName: "static-reviews" */ '../reviews/StaticReviews'));

type DynamicConfigEntry = {
  contentfulId: string;
  content: string;
};

interface Props {
  data: IPageData & {
    pageContent: IProductPage
  },
  productPage: IProductPage,
  isSelectorInitialized?: boolean;
  selector?: SelectorContent;
}

const ProductDetailAdvanced = ({
  data,
  productPage,
  isSelectorInitialized = true,
  selector,
}: Props) => {
  const {
    pageContent: {
      reviews: staticReviews,
      productReviews: reviewsIO,
      contentful_id: contentfulId,
      productDiscounts,
    },
  } = data;
  useSaveUserSelection();
  const configuration = getConfiguration();
  const { experiment, data: dynamicConfig } = usePagesExperiment<DynamicConfigEntry>('ninjas_exp1679_communicate_availability_of_templates', 'ninjas-exp1679_communicate_availability_of_templates', contentfulId);
  const isExperimentEnabled = experiment?.variation === 'variant' && dynamicConfig?.content;

  // Switch to Reviews.io when there are more than 3 reviews.
  const reviewSource = configuration.productReviews.enabled && reviewsIO.results_count >= REVIEWS_IO_THRESHOLD
    ? 'reviews_io'
    : 'static';
  const reviews = reviewSource === 'static' ? staticReviews : reviewsIO;
  const hasProductReviews = reviews.results_count > 0;
  const ref = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const loadingMessage = intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' });

  const handleClick = () => {
    if (ref.current) {
      scrollIntoView(ref.current, { behavior: 'smooth' });
    }
    dispatchDataLayerEntry('productDetailReviews', 'click');
  };

  return (
    <div className="pd-relative pd-z-10 pd-grid pd-grid-flow-row pd-grid-cols-1 pd-gap-9 md:pd-grid-cols-12" data-cy="product-page">
      <div className="pd-order-1 pd-col-span-6 md:pd-order-2 md:pd-col-span-7">
        <header className="product-page-title">
          <Heading as="h1">
            {productPage.title || productPage.productName || ''}
          </Heading>
          {productPage.subtitle && (
            <Heading as="div" variant="display" size="3" className="pd-text-dim-800">
              {productPage.subtitle}
            </Heading>
          )}
        </header>
        {hasProductReviews && (
          <LinkAsButton
            className="pd-my-4 pd-flex pd-items-center pd-font-normal"
            variant="secondary"
            onClick={handleClick}
          >
            <ReviewStars className="pd-mr-2" score={reviews.average_rating} />
            <FormattedMessage
              id="productDetail.reviews.total"
              defaultMessage="{total} Reviews"
              values={{ total: reviews.results_count }}
            />
          </LinkAsButton>
        )}
        {selector && selector.product.key && (
          <DiscountLabels productKey={selector.product.key} productDiscounts={productDiscounts} />
        )}
        <Experiment id="ninjas-exp1057_show_order_steps_on_pdp">
          <Experiment.Variant>
            <OrderProgress pageLayout={data.pageContent.pageLayout} />
          </Experiment.Variant>
        </Experiment>
        {productPage.introduction && (
          <section className="product-page-introduction pd-mt-4">
            <Markdown anchorTarget="_blank">{productPage.introduction.introduction}</Markdown>
          </section>
        )}
        <section className="product-page-selector">
          {isSelectorInitialized ? (
            <>
              {selector ? (
                <StepByStepSelectorSection selector={selector} data={data} />
              ) : (
                <div className="pd-mt-8">
                  <Empty
                    icon="exclamation"
                    title={intl.formatMessage({
                      id: 'product.configurator.outOfStock',
                      defaultMessage: 'This product is currently unavailable/out of stock',
                    })}
                    description={<FormattedMessage id="product.configurator.outOfStockDescription" defaultMessage="Contact our customer service or request a custom quote for an alternative" />}
                  />
                </div>
              )}
            </>
          ) : <FallbackSpinnerComponent loadingMessage={loadingMessage} />}
        </section>
      </div>
      <div className="pd-order-2 pd-col-span-6 md:pd-order-1 md:pd-col-span-5">
        {isExperimentEnabled && (
          <div className="pd-mb-6">
            <button
              type="button"
              className="pd-relative pd-flex pd-h-16 pd-w-full pd-cursor-default pd-flex-col pd-items-center pd-gap-5 pd-rounded-md pd-bg-dim-100 md:pd-flex-row md:pd-p-0"
              onClick={() => addDataLayerEntry('click_on_availability_of_templates_banner', {
                eventName: 'click_on_availability_of_templates_banner',
                experiment: 'EXP1679',
              })}
            >
              <div className="pd-flex pd-grow pd-flex-wrap pd-items-center pd-justify-center pd-gap-2">
                <Icon icon="photo-edit" />
                <span className="pd-font-semibold">
                  {dynamicConfig?.content}
                </span>
              </div>
            </button>
          </div>
        )}
        <Grid className="pd-grid pd-grid-cols-1 pd-gap-9">
          <Grid.Item>
            <figure className="product-page-gallery">
              <ProductGalleryBuilder
                title={productPage.title || productPage.productName}
                gallery={productPage.gallery}
                productKey={selector?.product.key}
                scenes={productPage.scenes}
              />
            </figure>
          </Grid.Item>
          {productPage?.gallery?.experimentId && (
            <Grid.Item>
              <figure className="product-page-gallery">
                <Experiment
                  id={productPage.gallery.experimentId}
                  fallback={<FallbackSpinnerComponent loadingMessage={loadingMessage} />}
                >
                  <Experiment.Control>
                    <ProductGalleryBuilder
                      title={productPage.title || productPage.productName}
                      gallery={productPage.gallery.control}
                      productKey={selector?.product.key}
                      scenes={productPage.scenes}
                    />
                  </Experiment.Control>
                  <Experiment.Variant>
                    <ProductGalleryBuilder
                      title={productPage.title || productPage.productName}
                      gallery={productPage.gallery.variant}
                      productKey={selector?.product.key}
                      scenes={productPage.scenes}
                    />
                  </Experiment.Variant>
                </Experiment>
              </figure>
            </Grid.Item>
          )}
          <Grid.Item>
            <PersonalAssistance
              source="PDP-advanced"
              text={<FormattedMessage id="general.personal.advice.text" defaultMessage="personal assistance" />}
            />
          </Grid.Item>
          <Grid.Item>
            <section className="product-page-seo-content pd-overflow-x-auto">
              <ProductTabs productPage={productPage} />
            </section>
          </Grid.Item>
          <ExploreDesignTemplateButton label="pdp_button" isButton className="pd-w-full" withLine={false} />
          {productPage.linkedFaqs?.length > 0 && (
            <Grid.Item>
              <section className="product-page-seo-content">
                <LinkedFaqs
                  linkedFaqs={productPage.linkedFaqs}
                  productName={productPage.productName}
                  contentful_id={productPage.contentful_id}
                  variantSku={selector?.configuration.variantSku}
                  pageName="product_detail_page"
                />
              </section>
            </Grid.Item>
          )}
          {productPage.productQuote && (
            <Grid.Item>
              <FeatureToggle id="create_quote_in_pdp">
                <FeatureToggle.Enabled>
                  <ClientOnly>
                    <Suspense>
                      <QuoteRequestSection productQuote={productPage.productQuote} />
                    </Suspense>
                  </ClientOnly>
                </FeatureToggle.Enabled>
              </FeatureToggle>
            </Grid.Item>
          )}
          {hasProductReviews && (
            <Grid.Item>
              <div ref={ref} className="product-page-reviews">
                {reviewSource === 'reviews_io' ? (
                  <ReviewsIO contentfulId={contentfulId} productReviews={reviews} />
                ) : (
                  <StaticReviews productReviews={reviews} />
                )}
              </div>
            </Grid.Item>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default ProductDetailAdvanced;
